import React, { useEffect, useState } from 'react';
import Chapter from '../../../Chapter';
import { GradualSpacing } from '../../../effects/GradualSpacing';
import setAnimationTimeout from '../../../../core/utils/setAnimationTimeout';

type ServiceHubDirectoryFloor = {
    title: string,
    zones: string[],
};

type ServiceHubDirectoryProps = {
    title: string,
    store_name: string,
    floors: ServiceHubDirectoryFloor[]
};

const STATIC_STATE_DELAY_MS = 8_000;
const ZONE_OFFSET_S = 0.2;

export default class ServiceHubDirectoryChapter extends Chapter<ServiceHubDirectoryProps> {
    component = () => {
        const [scale, setScale] = useState(1);
        const [isVisible, setIsVisible] = useState(true);

        const paddingBottom = 50;
        const headerHeight = 200;

        const checkOverflow = () => {
            const container = document.getElementById('directory-container');
            if (!container) {
                return;
            }

            if (container.scrollHeight <= container.clientHeight) {
                setScale(1);
                return;
            }

            setScale((container.clientHeight - headerHeight - paddingBottom) / container.scrollHeight);
        };

        useEffect(() => {
            checkOverflow();
            window.addEventListener('resize', checkOverflow);

            return () => {
                window.removeEventListener('resize', checkOverflow);
            };
        }, []);

        useEffect(() => {
            return setAnimationTimeout(() => setIsVisible(false), this.calculateAnimationDuration() * 1000 + STATIC_STATE_DELAY_MS);
        }, []);

        useEffect(() => {
            checkOverflow();
        }, [this.data]);

        const scaledFontSize = 96 / 19.2 * scale;
        const scaledZoneFontSize = 93 / 19.2 * scale;
        const scaledFloorsGap = 160 / 19.2 * scale;
        const scaledZonesGap = 28 / 19.2 * scale;

        return (
            <div
                id="directory-container"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: `2.6vh`, // 50px
                    minHeight: '100vh',
                    width: '100vw',
                    backgroundColor: 'black',
                    fontFamily: 'HelveticaNowDisplay',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    color: 'white',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '1.98vh', // 38px
                        lineHeight: '2.18vh', // 41.80px
                        letterSpacing: '0.04vh', // 0.76px
                        wordWrap: 'break-word'
                    }}
                >
                    <span>{this.data?.title}</span>
                    <span>{this.data?.store_name}</span>
                </div>
                <div style={{ marginTop: 'auto' }}>
                    {(this.data?.floors || []).map((floorData, idx) => (
                        <div style={{ marginTop: idx > 0 ? `${scaledFloorsGap}vh` : 0 }}>
                            <h2 style={{ fontSize: `${scaledFontSize}vh` }}>
                                <GradualSpacing
                                    key={encodeURIComponent(floorData.title)}
                                    text={floorData.title}
                                    offset={0.2 + idx * 0.2}
                                    isVisible={isVisible}
                                    style={{ display: 'inline-block' }}
                                />
                                &#8203;{/* empty character */}
                            </h2>
                            <div
                                style={{
                                    marginTop: `${scaledZonesGap}vh`,
                                    textAlign: 'right',
                                    fontSize: `${scaledZoneFontSize}vh`,
                                }}
                            >
                                {floorData.zones.map((zone, idx) => (
                                    <div>
                                        &#8203;{/* empty character */}
                                        <GradualSpacing
                                            key={`${encodeURIComponent(zone)}_${idx}`}
                                            text={zone}
                                            offset={idx * ZONE_OFFSET_S}
                                            isVisible={isVisible}
                                            style={{ display: 'inline-block' }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    calculateAnimationDuration(): number {
        const maxZonesFloor = (this.data?.floors || []).reduce((maxFloor, floor) =>
            floor.zones.length > maxFloor.zones.length ? floor : maxFloor
        );

        const maxZonesCount = maxZonesFloor.zones.length;

        const lastZone = maxZonesFloor.zones[maxZonesFloor.zones.length - 1];
        const lastZoneWordsCount = lastZone.trim() ? lastZone.split(" ").length : 0;

        return 1.5 + maxZonesCount * ZONE_OFFSET_S + lastZoneWordsCount * 0.2;
    }

    fonts = ['HelveticaNowDisplay'];

    calculateDuration() {
        return this.calculateAnimationDuration() * 2 + STATIC_STATE_DELAY_MS / 1000;
    }
};
